import { TZDate } from '@date-fns/tz';
import { addDays, addMinutes, format, isValid, parse, startOfDay } from 'date-fns';
/**
 * Toutes les dates métier (date de préparation, planifications, affichage) sont
 * exprimées en `Europe/Paris` ; le stockage reste en UTC (`01` §0).
 *
 * Aucun calcul de fuseau n'est fait à la main : `TZDate` (@date-fns/tz) porte le
 * fuseau, `date-fns` fait l'arithmétique. Les deux bascules d'heure d'été
 * françaises (dernier dimanche de mars et d'octobre) sont couvertes par les
 * tests.
 */
export const PARIS_TIME_ZONE = 'Europe/Paris';
/** Format d'affichage de l'interface — `02` §0 : `JJ/MM/AAAA HH:MM`. */
export const PARIS_DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm';
/** Format de date seule pour l'interface : `JJ/MM/AAAA`. */
export const PARIS_DATE_FORMAT = 'dd/MM/yyyy';
function toParis(instant) {
    return new TZDate(instant, PARIS_TIME_ZONE);
}
/** Vue `Europe/Paris` d'un instant UTC. */
export function parisDate(instant) {
    return toParis(instant);
}
/** `JJ/MM/AAAA HH:MM` en heure de Paris. */
export function formatParisDateTime(instant) {
    return format(toParis(instant), PARIS_DATE_TIME_FORMAT);
}
/** `JJ/MM/AAAA` en heure de Paris. */
export function formatParisDate(instant) {
    return format(toParis(instant), PARIS_DATE_FORMAT);
}
/**
 * Date de préparation Martelet (`IC1` col. 9) et date des noms de fichiers :
 * `AAAAMMJJ` en heure de Paris (`01` §2.2.h).
 */
export function formatPreparationDate(instant) {
    return format(toParis(instant), 'yyyyMMdd');
}
/**
 * Horodatage des noms de fichiers Martelet : `AAAAMMJJHHMMSS` (14 chiffres,
 * `01` §2.4), en heure de Paris comme en v1.
 */
export function formatMarteletTimestamp(instant) {
    return format(toParis(instant), 'yyyyMMddHHmmss');
}
/**
 * Instant UTC d'un horodatage de nom de fichier Martelet, lu en heure de
 * Paris : les 14 premiers chiffres (`AAAAMMJJHHMMSS`), les fichiers de retour
 * `O_COMMANDES` y ajoutant les millisecondes. `null` si le format ou la date
 * est invalide.
 */
export function parseMarteletTimestamp(value) {
    const match = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})\d{0,3}$/.exec(value);
    if (!match) {
        return null;
    }
    const [, year, month, day, hours, minutes, seconds] = match.map(Number);
    const candidate = new TZDate(year, month - 1, day, hours, minutes, seconds, 0, PARIS_TIME_ZONE);
    if (!isValid(candidate) || format(candidate, 'yyyyMMddHHmmss') !== value.slice(0, 14)) {
        return null;
    }
    return new Date(candidate.getTime());
}
/** Jour civil parisien d'un instant, au format ISO `AAAA-MM-JJ`. */
export function parisDayKey(instant) {
    return format(toParis(instant), 'yyyy-MM-dd');
}
/** Instant UTC correspondant à 00:00:00.000 du jour parisien de `instant`. */
export function startOfParisDay(instant) {
    return new Date(startOfDay(toParis(instant)).getTime());
}
/** Instant UTC correspondant à 23:59:59.999 du jour parisien de `instant`. */
export function endOfParisDay(instant) {
    const nextDayStart = startOfDay(addDays(toParis(instant), 1));
    return new Date(nextDayStart.getTime() - 1);
}
/**
 * Bornes UTC `[début, fin]` d'un jour parisien donné au format `AAAA-MM-JJ`.
 * Utilisé par les filtres de période des écrans (`02` §0) et par la sélection
 * des factures Pennylane par date (`01` §6.2).
 */
export function parisDayBounds(day) {
    const parsed = parseParisDayKey(day);
    return { start: startOfParisDay(parsed), end: endOfParisDay(parsed) };
}
/** Interprète `AAAA-MM-JJ` comme un jour parisien et renvoie son instant de début (UTC). */
export function parseParisDayKey(day) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!match) {
        throw new RangeError(`Jour invalide « ${day} » : format attendu AAAA-MM-JJ`);
    }
    const [, year, month, dayOfMonth] = match;
    const candidate = new TZDate(Number(year), Number(month) - 1, Number(dayOfMonth), 0, 0, 0, 0, PARIS_TIME_ZONE);
    if (!isValid(candidate) || format(candidate, 'yyyy-MM-dd') !== day) {
        throw new RangeError(`Jour invalide « ${day} »`);
    }
    return new Date(candidate.getTime());
}
/**
 * Analyse une date française `JJ/MM/AAAA` (note `delivery_date:` d'une commande,
 * `01` §2.2.h) et renvoie l'instant UTC du début de ce jour parisien.
 * Renvoie `null` si le format est invalide — l'appelant émet alors un
 * avertissement et retombe sur `processedAt`.
 */
export function parseFrenchDate(value) {
    const trimmed = value.trim();
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        return null;
    }
    const reference = new TZDate(0, PARIS_TIME_ZONE);
    const parsed = parse(trimmed, PARIS_DATE_FORMAT, reference);
    if (!isValid(parsed) || format(parsed, PARIS_DATE_FORMAT) !== trimmed) {
        return null;
    }
    return new Date(startOfDay(parsed).getTime());
}
/** Ajoute `minutes` à un instant (offset d'envoi, `01` §1.1). */
export function addMinutesUtc(instant, minutes) {
    return addMinutes(instant, minutes);
}
/** Ajoute `days` jours *civils parisiens* (robuste aux bascules d'heure d'été). */
export function addParisDays(instant, days) {
    return new Date(addDays(toParis(instant), days).getTime());
}
//# sourceMappingURL=paris.js.map